// What a failed load means to the user: which part failed (changelist's
// server, GitHub, the user's access, Changeling's own engine), in plain
// words, with the request it failed on for the Details a user can copy to
// support.
import { EngineError } from '../engine/error';
import { HttpError } from './cache';
import { GraphQLFailure, isSamlError } from './graphqlError';
import { NoResponseError, type RequestRecord } from './request';
import { serverErrorWords, type ServerErrorWords } from './serverErrors';
import { classifyLimit, describeRateLimit, RateLimitError, type RateLimit } from './rateLimit';

/**
 * - `unreachable`: changelist's server did not answer (no response, or a
 *   proxy in front of it answered 502, 503 or 504 on its behalf).
 * - `offline`: no response, and the browser says it is offline.
 * - `server_error`: changelist's server answered that it failed (500).
 * - `github_slow`: GitHub ran out of time (`upstream_timeout`).
 * - `github_error`: GitHub failed (`upstream_error`).
 * - `rate_limited`: changelist's or GitHub's rate limit.
 * - `signed_out`: the session is gone (401).
 * - `no_access`: GitHub says the user cannot see it, or it does not exist.
 * - `engine_bug`: the engine in the browser failed working it out (a
 *   panic, a trap, or input it could not read): a bug in Changeling.
 * - `engine_slow`: the engine ran past its time budget.
 * - `other`: anything else, worded by its own message.
 */
export type Cause =
  | 'unreachable'
  | 'offline'
  | 'server_error'
  | 'github_slow'
  | 'github_error'
  | 'rate_limited'
  | 'signed_out'
  | 'no_access'
  | 'engine_bug'
  | 'engine_slow'
  | 'other';

export interface Failure {
  cause: Cause;
  /** The error's own message. */
  message: string;
  /** The request it failed on, when known. */
  request: RequestRecord | null;
  /** The changelist server's typed error, when it answered with one (see serverErrors.ts). */
  server: ServerErrorWords | null;
  /** Which rate limit, until when, when the cause is `rate_limited`. */
  rateLimit: RateLimit | null;
  /** When the failure was seen (epoch ms). */
  at: number;
}

/** A changelist error response's `error` code, or null (e.g. a proxy's page). */
function changelistCode(body: unknown): string | null {
  const code = typeof body === 'object' && body !== null ? (body as { error?: unknown }).error : undefined;
  return typeof code === 'string' ? code : null;
}

const TIMEOUT = /time(d)? ?out|in time/i;

function causeOf(e: unknown, online: boolean): Cause {
  if (e instanceof EngineError) return e.kind === 'slow' ? 'engine_slow' : 'engine_bug';
  if (e instanceof NoResponseError) return online ? 'unreachable' : 'offline';
  if (e instanceof RateLimitError) return 'rate_limited';
  if (e instanceof GraphQLFailure) {
    if (e.errors.some((x) => TIMEOUT.test(x.message))) return 'github_slow';
    // SAML enforcement says what to do (authorize the session) in its own words.
    if (e.errors.some(isSamlError)) return 'other';
    if (e.errors.some((x) => x.type === 'NOT_FOUND' || x.type === 'FORBIDDEN')) return 'no_access';
    return 'other';
  }
  if (!(e instanceof HttpError)) return 'other';
  const code = changelistCode(e.body);
  switch (e.status) {
    case 401:
      return 'signed_out';
    case 403:
    case 404:
      return code === null || code === 'not_found' ? 'no_access' : 'other';
    case 429:
      return 'rate_limited';
    case 500:
      return 'server_error';
    case 502:
    case 503:
    case 504:
      if (code === 'upstream_timeout') return 'github_slow';
      if (code === 'upstream_error') return 'github_error';
      if (code !== null) return 'other';
      // The bridge types every GitHub 5xx, so one without a code is a
      // proxy answering for changelist's server.
      return 'unreachable';
    default:
      return 'other';
  }
}

/** The failure `e` means; `online` is the browser's `navigator.onLine` when it failed. */
export function failureOf(
  e: unknown,
  now = Date.now(),
  online = typeof navigator === 'undefined' || navigator.onLine !== false,
): Failure {
  return {
    cause: causeOf(e, online),
    message: e instanceof Error ? e.message : String(e),
    request: e instanceof HttpError || e instanceof NoResponseError ? e.request : null,
    server: e instanceof HttpError ? serverErrorWords(e.body) : null,
    rateLimit: rateLimitOf(e, now),
    at: now,
  };
}

function rateLimitOf(e: unknown, now: number): RateLimit | null {
  if (e instanceof RateLimitError) return e.limit;
  if (e instanceof HttpError)
    return classifyLimit({ status: e.status, body: e.body, retryAfter: e.retryAfter, resetAt: e.resetAt, budget: e.budget }, now);
  return null;
}

/** Whether the failure may pass on its own, so trying again soon is worth it. */
export function isTransient(f: Failure): boolean {
  return retriesFor(f) > 0;
}

/**
 * How many times a load that failed so is tried again on its own: twice
 * when changelist's server or GitHub failed or can't be reached, once when
 * GitHub was too slow (GitHub charges a query that timed out against the
 * rate limit, and a slow query tends to stay slow), else never.
 */
export function retriesFor(f: Failure): number {
  switch (f.cause) {
    case 'unreachable':
    case 'offline':
    case 'github_error':
      return 2;
    case 'github_slow':
      return 1;
    default:
      return 0;
  }
}

const capitalized = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const sentence = (s: string) => (/[.!?]$/.test(s) ? s : `${s}.`);

/**
 * What failed and what to do, for loading `what` (a noun phrase: "this
 * section's search", "this pull request"). A rate limit's words count down
 * to `now`, so a view showing them re-reads them as time passes, and say
 * the load is tried again by itself only when `retries` (the view does so).
 */
export function explain(f: Failure, what: string, now = Date.now(), retries = false): { summary: string; advice: string | null } {
  switch (f.cause) {
    case 'unreachable':
      return {
        summary: `Changeling's server isn't reachable, so ${what} couldn't load.`,
        advice: 'Check your connection, then try again. If it keeps failing, the server may be down.',
      };
    case 'offline':
      return {
        summary: `You're offline, so ${what} couldn't load.`,
        advice: 'Check your connection; Changeling tries again when it is back.',
      };
    case 'server_error':
      return {
        summary: `Changeling's server ran into a problem loading ${what}.`,
        advice: 'Try again. If it keeps happening, send the details below to whoever runs changelist.',
      };
    case 'github_slow':
      return {
        summary: `GitHub took too long to answer ${what}.`,
        advice: 'This is usually brief; try again in a moment.',
      };
    case 'github_error':
      return {
        summary: `GitHub couldn't answer ${what} just now.`,
        advice: 'This is usually brief; try again in a moment. If it persists, check githubstatus.com.',
      };
    case 'rate_limited': {
      if (!f.rateLimit) return { summary: f.message, advice: null };
      const words = describeRateLimit(f.rateLimit, now, retries);
      return { summary: `Couldn't load ${what}: ${words.summary} ${words.next}`, advice: words.advice };
    }
    case 'signed_out':
      return { summary: `You're signed out, so ${what} couldn't load.`, advice: 'Sign in again.' };
    case 'no_access':
      return {
        summary: `GitHub says you can't see ${what}, or it no longer exists.`,
        advice: "Check that you're signed in with the GitHub account that has access.",
      };
    case 'engine_bug':
      return {
        summary: `Changeling hit a bug working out ${what}.`,
        advice: 'Try again. If it keeps happening, send feedback with the details below.',
      };
    case 'engine_slow':
      return {
        summary: `Changeling took too long working out ${what}.`,
        advice: 'Try again. If it keeps happening, it may be too large to work out in time.',
      };
    case 'other':
      if (f.server) return { summary: f.server.text, advice: f.server.advice };
      return { summary: sentence(`${capitalized(what)} couldn't load: ${f.message}`), advice: null };
  }
}

/** The failure as plain text for support: the request, its status, the time and the request ID. */
export function formatDetails(f: Failure): string {
  const r = f.request;
  const lines = r
    ? [
        `Request: ${r.method} ${r.path}`,
        `Status: ${r.status === null ? 'no response' : `HTTP ${r.status}`}`,
        `Time: ${new Date(r.at).toISOString()}`,
        `Request ID: ${r.requestId}`,
      ]
    : [`Time: ${new Date(f.at).toISOString()}`];
  return [...lines, `Error: ${f.message}`].join('\n');
}
